import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, ArrowRight, Briefcase, Megaphone, Mic2, Sparkles, Globe2, Heart, Scale, Flame, Check } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";

const USE_CASES = [
    { id: "interview", icon: Briefcase, title: "Interview prep", note: "Behavioral, technical, closing questions" },
    { id: "pitch", icon: Megaphone, title: "Pitch & sales", note: "Yourself, your product, your idea" },
    { id: "public_speaking", icon: Mic2, title: "Public speaking", note: "Talks, toasts, persuasion" },
    { id: "all", icon: Sparkles, title: "A bit of everything", note: "I'll mix it up" },
];

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "hi", label: "Hindi" },
    { code: "pt", label: "Portuguese" },
    { code: "it", label: "Italian" },
    { code: "ja", label: "Japanese" },
    { code: "zh", label: "Chinese" },
];

const TONES = [
    { id: "warm", icon: Heart, title: "Warm", note: "Encouraging — lead with what works" },
    { id: "balanced", icon: Scale, title: "Balanced", note: "Honest, with a clear next step", recommended: true },
    { id: "strict", icon: Flame, title: "Strict", note: "Direct — don't soften it" },
];

export default function Onboarding() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [useCase, setUseCase] = useState("all");
    const [language, setLanguage] = useState("en");
    const [tone, setTone] = useState("balanced");
    const [submitting, setSubmitting] = useState(false);

    if (!user) return <Navigate to="/auth" replace />;
    if (user.onboarded) return <Navigate to="/home" replace />;

    const next = () => setStep((s) => s + 1);
    const back = () => setStep((s) => Math.max(0, s - 1));

    const finish = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/onboarding", {
                use_case: useCase,
                coach_tone: tone,
                coach_language: language,
            });
            refresh?.({ ...user, onboarded: true });
            toast.success("You're set");
            navigate(`/practice/${data.first_prompt.id}`, { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your setup");
            setSubmitting(false);
        }
    };

    const skip = async () => {
        try {
            await api.post("/auth/onboarding", {
                use_case: "all",
                coach_tone: "balanced",
                coach_language: "en",
            });
            refresh?.({ ...user, onboarded: true });
        } catch { /* non-fatal */ }
        navigate("/home", { replace: true });
    };

    const totalSteps = 4;
    const progress = ((step + 1) / totalSteps) * 100;

    return (
        <div className="min-h-screen vf-grain flex flex-col" data-testid="onboarding-page">
            {/* Top bar */}
            <div className="px-5 sm:px-8 pt-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-terracotta flex items-center justify-center text-white">
                        <Mic size={14} strokeWidth={1.5} />
                    </div>
                    <span className="font-serif text-2xl tracking-tight">Cadence</span>
                </div>
                <button
                    onClick={skip}
                    className="text-xs tracking-[0.2em] uppercase font-bold text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
                    data-testid="onboarding-skip"
                >
                    Skip
                </button>
            </div>

            {/* Progress */}
            <div className="px-5 sm:px-8 mt-6">
                <div className="h-1 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden max-w-md mx-auto">
                    <div
                        className="h-full bg-terracotta rounded-full"
                        style={{ width: `${progress}%`, transition: "width 500ms cubic-bezier(0.16,1,0.3,1)" }}
                        data-testid="onboarding-progress"
                    />
                </div>
                <div className="text-center text-[10px] tracking-[0.25em] uppercase font-bold text-stone-400 mt-2">
                    Step {step + 1} of {totalSteps}
                </div>
            </div>

            {/* Steps */}
            <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10">
                <div className="w-full max-w-xl">
                    <AnimatePresence mode="wait">
                        {/* Step 0: Welcome */}
                        {step === 0 && (
                            <motion.div
                                key="welcome"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.4 }}
                                className="text-center"
                                data-testid="onboarding-step-0"
                            >
                                <div className="inline-flex w-16 h-16 rounded-3xl bg-terracotta items-center justify-center text-white mb-6">
                                    <Mic size={28} strokeWidth={1.5} />
                                </div>
                                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight font-light leading-tight">
                                    Welcome, {user.name?.split(" ")[0] || "there"}.
                                </h1>
                                <p className="font-serif text-2xl sm:text-3xl tracking-tight font-light italic text-stone-500 mt-3">
                                    Thirty seconds to set you up.
                                </p>
                                <p className="text-sm sm:text-base text-stone-600 dark:text-stone-400 mt-6 max-w-md mx-auto leading-relaxed">
                                    Cadence is private, honest, and made for the moments where the right words matter. Let's tune your coach to you.
                                </p>
                                <Button
                                    onClick={next}
                                    className="rounded-full bg-terracotta hover:bg-[#D05A40] text-white px-8 h-12 mt-8"
                                    data-testid="onboarding-begin"
                                >
                                    Let's begin <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </motion.div>
                        )}

                        {/* Step 1: Use case */}
                        {step === 1 && (
                            <motion.div
                                key="usecase"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.4 }}
                                data-testid="onboarding-step-1"
                            >
                                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">First question</div>
                                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light mt-2">What brings you here?</h2>
                                <p className="text-sm text-stone-500 mt-1">We'll pick your first prompt based on this.</p>
                                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {USE_CASES.map(({ id, icon: Icon, title, note }) => (
                                        <button
                                            key={id}
                                            onClick={() => setUseCase(id)}
                                            data-testid={`usecase-${id}`}
                                            className={`text-left rounded-3xl border p-5 transition-all
                                                ${useCase === id
                                                    ? "border-terracotta bg-terracotta/5 ring-1 ring-terracotta/30"
                                                    : "border-stone-200 dark:border-stone-700 hover:border-stone-400"}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center
                                                    ${useCase === id ? "bg-terracotta text-white" : "bg-stone-100 dark:bg-stone-800 text-stone-500"}`}>
                                                    <Icon size={18} strokeWidth={1.5} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-medium">{title}</div>
                                                    <div className="text-xs text-stone-500 mt-0.5">{note}</div>
                                                </div>
                                                {useCase === id && <Check size={16} className="text-terracotta shrink-0" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <NavRow onBack={back} onNext={next} nextLabel="Continue" />
                            </motion.div>
                        )}

                        {/* Step 2: Language */}
                        {step === 2 && (
                            <motion.div
                                key="language"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.4 }}
                                data-testid="onboarding-step-2"
                            >
                                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400 flex items-center gap-2">
                                    <Globe2 size={12} strokeWidth={1.5} /> Language
                                </div>
                                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light mt-2">What language will you practice in?</h2>
                                <p className="text-sm text-stone-500 mt-1">Affects transcription accuracy and the voice of your feedback.</p>
                                <div className="mt-6 grid grid-cols-3 sm:grid-cols-3 gap-2">
                                    {LANGUAGES.map((l) => (
                                        <button
                                            key={l.code}
                                            onClick={() => setLanguage(l.code)}
                                            data-testid={`lang-${l.code}`}
                                            className={`px-3 py-3 rounded-2xl text-sm font-medium border transition-colors
                                                ${language === l.code
                                                    ? "border-terracotta bg-terracotta/10 text-terracotta"
                                                    : "border-stone-200 dark:border-stone-700 hover:border-stone-400"}`}
                                        >
                                            {l.label}
                                        </button>
                                    ))}
                                </div>
                                <NavRow onBack={back} onNext={next} nextLabel="Continue" />
                            </motion.div>
                        )}

                        {/* Step 3: Tone */}
                        {step === 3 && (
                            <motion.div
                                key="tone"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.4 }}
                                data-testid="onboarding-step-3"
                            >
                                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">Your coach</div>
                                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light mt-2">How tough should your coach be?</h2>
                                <p className="text-sm text-stone-500 mt-1">You can change this anytime in Settings.</p>
                                <div className="mt-6 space-y-3">
                                    {TONES.map(({ id, icon: Icon, title, note, recommended }) => (
                                        <button
                                            key={id}
                                            onClick={() => setTone(id)}
                                            data-testid={`tone-${id}`}
                                            className={`w-full text-left rounded-3xl border p-5 transition-all flex items-center gap-4
                                                ${tone === id
                                                    ? "border-terracotta bg-terracotta/5 ring-1 ring-terracotta/30"
                                                    : "border-stone-200 dark:border-stone-700 hover:border-stone-400"}`}
                                        >
                                            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0
                                                ${tone === id ? "bg-terracotta text-white" : "bg-stone-100 dark:bg-stone-800 text-stone-500"}`}>
                                                <Icon size={18} strokeWidth={1.5} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium flex items-center gap-2">
                                                    {title}
                                                    {recommended && <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-sage bg-sage/10 px-2 py-0.5 rounded-full">Recommended</span>}
                                                </div>
                                                <div className="text-xs text-stone-500 mt-0.5">{note}</div>
                                            </div>
                                            {tone === id && <Check size={16} className="text-terracotta shrink-0" />}
                                        </button>
                                    ))}
                                </div>
                                <NavRow
                                    onBack={back}
                                    onNext={finish}
                                    nextLabel={submitting ? "Setting up…" : "Take my first rep"}
                                    nextDisabled={submitting}
                                    nextTestId="onboarding-finish"
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

function NavRow({ onBack, onNext, nextLabel, nextDisabled, nextTestId }) {
    return (
        <div className="mt-8 flex items-center justify-between gap-3">
            <Button
                variant="ghost"
                onClick={onBack}
                className="rounded-full text-stone-500"
                data-testid="onboarding-back"
            >
                Back
            </Button>
            <Button
                onClick={onNext}
                disabled={nextDisabled}
                className="rounded-full bg-terracotta hover:bg-[#D05A40] text-white px-7 h-11"
                data-testid={nextTestId || "onboarding-next"}
            >
                {nextLabel} <ArrowRight size={14} className="ml-2" />
            </Button>
        </div>
    );
}
